import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import * as auditApi from '../api/audit';
import { toast } from '../components/common/Toast';
import Modal from '../components/common/Modal';
import { useAuditCsvExport } from '../hooks/useAuditCsvExport';
import './Pages.css';

const LIMITS = [50, 100, 200];

// ISS-035 — 감사 로그는 super_admin 전용이므로 role 별 분기/타이틀 변경/CSV 조건부 노출이 불필요.
// 라우트 가드(App.tsx::AuditLogRoute) 가 진입을 차단하고, BE require_roles(SUPER_ADMIN) 이 403 을 반환한다.
// ISS-039 — CSV 내보내기 흐름 전면 재설계 (window.open 제거, blob 다운로드, 413 모달, 단일사용 + TTL).

const AuditLogPage: React.FC = () => {
  const [items, setItems] = useState<auditApi.AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [actionFilter, setActionFilter] = useState('');
  const [actions, setActions] = useState<auditApi.AuditAction[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const [csvDateFrom, setCsvDateFrom] = useState('');
  const [csvDateTo, setCsvDateTo] = useState('');

  // ISS-051 — 상세 로그 모달 (raw 정보)
  const [detailItem, setDetailItem] = useState<auditApi.AuditLogEntry | null>(null);

  // ISS-039 — CSV export 훅 (폴링 없음, blob 다운로드, 413 모달)
  const { exporting, limitExceededMessage, clearLimitExceededMessage, start: startExport } =
    useAuditCsvExport();

  useEffect(() => {
    auditApi.getActions().then(setActions).catch(() => {});
  }, []);

  // ISS-027: 모든 필터 state 를 deps 로 포함 — 수동 "조회" 버튼 클릭 시 최신 dateFrom/dateTo 보장
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: auditApi.AuditListParams = { page, limit };
      if (actionFilter) params.action = actionFilter;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const res = await auditApi.getList(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        toast.error(err.response?.data?.error || '로그 조회 실패');
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, actionFilter, dateFrom, dateTo]);

  // ISS-027: 날짜 필터(dateFrom/dateTo) 는 "조회" 버튼으로만 수동 조회 — 자동 트리거에서 의도적으로 제외.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLogs(); }, [page, limit, actionFilter]);

  const totalPages = Math.ceil(total / limit);

  const handleExport = async () => {
    if (!csvDateFrom || !csvDateTo) {
      toast.error('CSV 내보내기 날짜 범위를 지정해주세요');
      return;
    }
    await startExport({
      date_from: new Date(csvDateFrom).toISOString(),
      date_to: new Date(csvDateTo).toISOString(),
    });
  };

  return (
    <div>
      <div className="page-header">
        <h2>감사 로그</h2>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}>
          <option value="">전체 액션</option>
          {actions.map((a) => (
            <option key={a.value} value={a.value}>{a.name}</option>
          ))}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        <button className="btn btn--primary" onClick={() => { setPage(1); fetchLogs(); }}>
          조회
        </button>
        <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
          {LIMITS.map((l) => (
            <option key={l} value={l}>{l}건</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, padding: 12, background: '#f9f9f9', borderRadius: 6 }}>
        <span style={{ fontSize: 13, lineHeight: '32px' }}>CSV:</span>
        <input
          type="date"
          value={csvDateFrom}
          onChange={(e) => setCsvDateFrom(e.target.value)}
          disabled={exporting}
        />
        <input
          type="date"
          value={csvDateTo}
          onChange={(e) => setCsvDateTo(e.target.value)}
          disabled={exporting}
        />
        <button className="btn" onClick={handleExport} disabled={exporting}>
          {exporting ? '내보내는 중...' : '내보내기'}
        </button>
      </div>

      {/* ISS-039 — 한도 초과 (413) 모달 */}
      <Modal
        isOpen={limitExceededMessage !== null}
        onClose={clearLimitExceededMessage}
        title="CSV 내보내기 한도 초과"
        cancelText="확인"
      >
        <p style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
          {limitExceededMessage}
        </p>
      </Modal>

      {/* ISS-051 — 상세 로그 모달 (raw 정보) */}
      <Modal
        isOpen={detailItem !== null}
        onClose={() => setDetailItem(null)}
        title="감사 로그 상세"
        cancelText="닫기"
      >
        {detailItem && (
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <DetailRow label="ID" value={String(detailItem.id)} />
                <DetailRow
                  label="시간"
                  value={`${new Date(detailItem.timestamp).toLocaleString()} (${detailItem.timestamp})`}
                />
                <DetailRow label="액션" value={detailItem.action} />
                <DetailRow label="행위자 ID" value={detailItem.actor_id || '-'} />
                <DetailRow label="행위자 역할" value={detailItem.actor_role || '-'} />
                <DetailRow label="행위자 라벨" value={detailItem.actor_label || '-'} />
                <DetailRow label="대상 타입" value={detailItem.target_type || '-'} />
                <DetailRow label="대상 ID" value={detailItem.target_id || '-'} />
                <DetailRow label="대상 라벨" value={detailItem.target_label || '-'} />
                <DetailRow label="장소 ID" value={detailItem.apartment_id || '-'} />
                <DetailRow label="HTTP 메서드" value={detailItem.http_method || '-'} />
                <DetailRow label="HTTP 경로" value={detailItem.http_path || '-'} />
                <DetailRow label="HTTP 상태" value={detailItem.http_status?.toString() || '-'} />
                <DetailRow label="IP 주소" value={detailItem.ip_address || '-'} />
                <DetailRow label="User Agent" value={detailItem.user_agent || '-'} multiline />
                <tr>
                  <td style={{ fontWeight: 600, padding: '4px 8px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    Payload
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    {detailItem.payload_summary ? (
                      <pre
                        style={{
                          background: '#f5f5f5',
                          padding: 8,
                          borderRadius: 4,
                          fontSize: 11,
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {JSON.stringify(detailItem.payload_summary, null, 2)}
                      </pre>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {loading ? (
        <p>로딩 중...</p>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>시간</th>
                <th>액션</th>
                <th>행위자</th>
                <th>대상</th>
                <th>상태</th>
                <th>IP</th>
                <th>상세</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {new Date(item.timestamp).toLocaleString()}
                  </td>
                  <td>
                    <span style={{
                      fontSize: 11,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: '#e8f5e9',
                    }}>
                      {item.action}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {/* ISS-050 — actor_label 우선, 없으면 actor_role 폴백 */}
                    {item.actor_label || item.actor_role || '-'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {/* ISS-050 — target_label 우선, 없으면 target_type 폴백 */}
                    {item.target_label || item.target_type || '-'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {item.http_status || '-'}
                  </td>
                  <td style={{ fontSize: 11, color: '#999' }}>
                    {item.ip_address || '-'}
                  </td>
                  <td>
                    {/* ISS-051 — 상세 로그 보기 */}
                    <button
                      className="btn"
                      style={{ fontSize: 11, padding: '2px 8px' }}
                      onClick={() => setDetailItem(item)}
                    >
                      상세 보기
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                    조회된 로그가 없습니다
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
            <span style={{ lineHeight: '32px', fontSize: 13 }}>
              {page} / {totalPages || 1} (총 {total}건)
            </span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>다음</button>
          </div>
        </>
      )}
    </div>
  );
};

// ISS-051 — 상세 모달의 표 행 (재사용)
const DetailRow: React.FC<{ label: string; value: string; multiline?: boolean }> = ({
  label,
  value,
  multiline,
}) => (
  <tr>
    <td
      style={{
        fontWeight: 600,
        padding: '4px 8px',
        verticalAlign: 'top',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </td>
    <td
      style={{
        padding: '4px 8px',
        wordBreak: multiline ? 'break-all' : 'normal',
      }}
    >
      {value}
    </td>
  </tr>
);

export default AuditLogPage;
